import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Avatar,
  Button,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  Add,
  Close
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { eventApi, requestApi, UserRequest, userApi, User, Tag, tagApi, authApi } from '../utils/api';
import { Event } from '../types/event';
import EventCard from '../components/EventCard';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import SignupModal from '../components/auth/SignupModal';
import { getAvatarProps, AVAILABLE_SPORTS } from '../utils/userUtils';
import { eventBus, EVENTS } from '../utils/events';
import { Chip } from '@mui/material';

const Profile: React.FC = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [upcomingGoingEvents, setUpcomingGoingEvents] = useState<Array<{ event: Event, status: string }>>([]);
  const [upcomingHostingEvents, setUpcomingHostingEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const lastFetchedUserId = useRef<string | null>(null);

  // Always fetch fresh user data when Profile page mounts or when user ID changes
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const userIdChanged = lastFetchedUserId.current !== user.id;
      
      // Fetch if user ID changed (new user or user was updated)
      if (userIdChanged && !isFetchingUser) {
        setIsFetchingUser(true);
        lastFetchedUserId.current = user.id;
        
        // Always fetch fresh user data when Profile page mounts or user changes
        authApi.getCurrentUser()
          .then(updatedUser => {
            // Update with the fetched data to ensure we have the latest
            // Ensure sports data is properly structured
            if (updatedUser.sports && Array.isArray(updatedUser.sports)) {
              // Normalize sports data structure
              updatedUser.sports = updatedUser.sports.map((sport: any) => {
                if (typeof sport === 'object' && sport !== null) {
                  return {
                    sport_name: sport.sport_name || sport.sportName || sport.name,
                    skill_level: sport.skill_level || sport.skillLevel || sport.level,
                    preferences: sport.preferences || []
                  };
                }
                return sport;
              });
            }
            updateUser(updatedUser);
            setIsFetchingUser(false);
          })
          .catch(error => {
            console.error('Error fetching updated user data:', error);
            setIsFetchingUser(false); // Allow retry
            lastFetchedUserId.current = null; // Reset to allow retry
          });
      }
    }
  }, [isAuthenticated, user?.id, updateUser, isFetchingUser]);

  useEffect(() => {
    if (user && !isFetchingUser) {
      loadUserEvents();

      // Fetch tags for sports display
      const fetchTags = async () => {
        try {
          setTagsLoading(true);
          const allTags = await tagApi.getTags();
          setTags(allTags);
        } catch (error) {
          console.error('Error fetching tags:', error);
        } finally {
          setTagsLoading(false);
        }
      };
      fetchTags();

      // Listen for event updates (joins/cancels from other components)
      const unsubscribe = eventBus.on(EVENTS.EVENT_UPDATED, () => {
        loadUserEvents();
      });

      return () => {
        unsubscribe();
      };
    }
  }, [user]);



  const loadUserEvents = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Get user requests and user's created events
      // Add timestamp to prevent caching
      const [userRequests, myCreatedEvents]: [UserRequest[], Event[]] = await Promise.all([
        requestApi.getUserRequests(),
        eventApi.getMyEvents()
      ]);

      const now = new Date();

      // Process Hosting Events
      const upcomingHosting = myCreatedEvents.filter(event => new Date(event.time) > now);
      setUpcomingHostingEvents(upcomingHosting);
      // Process Going Events
      const allUserEventData: Array<{ eventId: string, status: string }> = [];

      console.log('🔍 User Requests:', userRequests);

      userRequests.forEach((request: UserRequest) => {
        if (request.user_id === user.id) {
          allUserEventData.push({
            eventId: request.event_id,
            status: request.status
          });
        }
      });

      // Get the actual event objects for all user requests
      const goingEventsPromises = allUserEventData.map(async ({ eventId, status }) => {
        try {
          const event = await eventApi.getEventById(eventId);
          return { event, status };
        } catch (err) {
          console.warn(`Failed to fetch event ${eventId}:`, err);
          return null;
        }
      });

      const goingEventsResults = await Promise.all(goingEventsPromises);
      const validGoingEvents = goingEventsResults.filter((item): item is { event: Event, status: string } => item !== null);

      console.log('🔍 Going Events Data:', validGoingEvents);

      // Filter Upcoming Going: Future AND (Accepted OR Submitted) - Hide Declined/Canceled/Expired
      const upcomingGoing = validGoingEvents.filter(item => {
        const isFuture = new Date(item.event.time) > now;
        const isValidStatus = item.status === 'ACCEPTED' || item.status === 'SUBMITTED';
        return isFuture && isValidStatus;
      });

      // Filter Past Going: Past events (regardless of status, or maybe just accepted? User said "previously joined")
      // Let's show all past interactions for now, or maybe just Accepted/Expired?
      // User said "previously joined", implying they actually went.
      // But "Past events" usually implies history.
      // Let's include everything that is expired.
      const pastGoing = validGoingEvents.filter(item => new Date(item.event.time) <= now);

      setUpcomingGoingEvents(upcomingGoing);

      setError(null);
    } catch (err) {
      setError('Failed to load your events');
      console.error('Error loading user events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = async () => {
    setShowEditModal(false);
    
    // Fetch updated user data and update AuthContext
    try {
      const updatedUser = await authApi.getCurrentUser();
      updateUser(updatedUser);
      
      // Also reload events to ensure everything is up to date
      await loadUserEvents();
    } catch (error) {
      console.error('Error fetching updated user:', error);
      // Fallback to reload if fetch fails
      window.location.reload();
    }
  };

  const handleEventClick = (eventId: string) => {
    navigate(`/event/${eventId}`);
  };

  if (!isAuthenticated || !user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Please log in to view your profile.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Profile Header */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, width: { xs: '100%', sm: 'auto' } }}>
            <Box>
              <Avatar
                {...getAvatarProps(user, 104)}
                sx={{
                  width: { xs: 80, sm: 96, md: 104 },
                  height: { xs: 80, sm: 96, md: 104 },
                  backgroundColor: '#533B7D',
                  fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
                  ...getAvatarProps(user, 104).sx
                }}
              />
            </Box>

            <Box sx={{ pt: { xs: 0, sm: 1 }, flex: 1 }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 'bold', 
                color: '#333', 
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
              }}>
                {user.display_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Member since {new Date(user.created_at || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' }
          }}>
            <Button
              onClick={handleEditClick}
              startIcon={<EditIcon />}
              variant="contained"
              sx={{
                backgroundColor: '#533B7D',
                textTransform: 'none',
                px: { xs: 2, sm: 3 },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Edit Profile
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Profile Picture Upload Section */}
        {showProfileUpload && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <ProfilePictureUpload onUploadSuccess={() => {
              loadUserEvents();
              setShowProfileUpload(false);
            }} />
          </Box>
        )}
      </Paper>

      {/* About Me Section */}
      <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          About me
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {user.bio || 'No bio yet. Click Edit Profile to add one.'}
        </Typography>
      </Paper>

      {/* Gallery Section */}
      <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Gallery
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            {[
              user.gallery_image_1,
              user.gallery_image_2,
              user.gallery_image_3,
              user.gallery_image_4
            ].filter(Boolean).map((image, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={image || ''}
                    alt={`Gallery ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 2
                    }}
                  />
                </Box>
              </Grid>
            ))}
            {[
              user.gallery_image_1,
              user.gallery_image_2,
              user.gallery_image_3,
              user.gallery_image_4
            ].filter(Boolean).length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    No images in gallery. Click Edit Profile to add some.
                  </Typography>
                </Grid>
              )}
          </Grid>
        </Box>
      </Paper>

      {/* My Interests Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          My interests
        </Typography>

        {user.sports && user.sports.length > 0 ? (
          <Grid container spacing={2}>
            {user.sports.map((sport, index) => {
              const sportInfo = AVAILABLE_SPORTS.find(s => s.name === sport.sport_name) ||
                AVAILABLE_SPORTS.find(s => s.id === sport.sport_name.toLowerCase());

              // Get tag names from IDs
              const preferenceTags = (sport.preferences || [])
                .map(prefId => {
                  // Skip null/undefined preferences
                  if (!prefId) return null;
                  // Handle if prefId is an object (from api interface) or string (from backend)
                  const id = typeof prefId === 'object' ? (prefId as any).id : prefId;
                  if (!id) return null;
                  const tag = tags.find(t => t.id === id);
                  return tag ? tag.name : id;
                })
                .filter((tag): tag is string => tag !== null && tag !== undefined);

              const displayTags = preferenceTags.slice(0, 3);
              const remainingTags = preferenceTags.length - 3;

              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontSize: 24, mr: 1 }}>
                        {sportInfo?.icon || '🏅'}
                      </Typography>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {sport.sport_name}
                        </Typography>
                        <Typography variant="body2" color="primary" fontWeight="medium">
                          {sport.skill_level}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {displayTags.map((tagName, i) => (
                        <Chip
                          key={i}
                          label={tagName}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      ))}
                      {remainingTags > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                          +{remainingTags} more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No sports interests added yet. Click Edit Profile to add some.
          </Typography>
        )}
      </Paper>

      {/* Events Section */}
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            My Events
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              size="small"
              onClick={() => navigate('/my-events')}
              sx={{ textTransform: 'none', color: '#533B7D' }}
            >
              View all
            </Button>
            <Button
              size="small"
              onClick={loadUserEvents}
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Tabs for Going and Hosting */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            mb: 3,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500
            }
          }}
        >
          <Tab
            label="Going"
            sx={{
              '&.Mui-selected': { color: '#533B7D' }
            }}
          />
          <Tab
            label="Hosting"
            sx={{
              '&.Mui-selected': { color: '#533B7D' }
            }}
          />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#533B7D' }} />
          </Box>
        ) : (
          <>
            {/* Going Events Tab */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Upcoming Going */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#533B7D' }}>
                    Upcoming
                  </Typography>
                  {upcomingGoingEvents.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {upcomingGoingEvents.map(({ event, status }) => (
                        <Box key={event.id} sx={{ position: 'relative' }}>
                          <EventCard
                            event={event}
                            onClick={() => handleEventClick(event.id)}
                            variant="horizontal"
                            showStatus={true}
                            userStatus={status === 'SUBMITTED' ? 'Pending' : status}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No upcoming events.
                    </Typography>
                  )}
                </Box>


              </Box>
            )}

            {/* Hosting Events Tab */}
            {activeTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Upcoming Hosting */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#533B7D' }}>
                    Upcoming
                  </Typography>
                  {upcomingHostingEvents.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {upcomingHostingEvents.map((event) => (
                        <Box key={event.id} sx={{ position: 'relative' }}>
                          <EventCard
                            event={event}
                            onClick={() => handleEventClick(event.id)}
                            variant="horizontal"
                            isHost={true}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No upcoming events hosted.
                    </Typography>
                  )}
                </Box>


              </Box>
            )}

          </>
        )}
      </Paper>
      {/* Edit Profile Modal */}
      <SignupModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSignupSuccess={handleEditSuccess}
        editMode={true}
        userData={user}
      />
    </Container>
  );
};

export default Profile;
